import { useState, useEffect } from 'react';
import { auth, db } from '@/lib/firebase';
import { collection, query, orderBy, onSnapshot, doc, deleteDoc } from 'firebase/firestore';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Star, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from 'sonner';

interface Rating {
  id: string;
  rating: number;
  comment: string;
  authorId: string;
  authorName: string;
  createdAt: any;
}

interface RatingsSectionProps {
  providerId: string;
}

export function RatingsSection({ providerId }: RatingsSectionProps) {
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [loading, setLoading] = useState(true);
  const [ratingToDelete, setRatingToDelete] = useState<Rating | null>(null);
  const currentUserId = auth.currentUser?.uid;

  useEffect(() => {
    const ratingsRef = collection(db, `users/${providerId}/ratings`);
    const q = query(ratingsRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ratingsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Rating[];
      setRatings(ratingsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [providerId]);

  const handleDeleteRating = async () => {
    if (!ratingToDelete) return;

    try {
      await deleteDoc(doc(db, `users/${providerId}/ratings/${ratingToDelete.id}`));
      toast.success('Avaliação excluída com sucesso');
    } catch (error) {
      console.error('Error deleting rating:', error);
      toast.error('Erro ao excluir avaliação');
    } finally {
      setRatingToDelete(null);
    }
  };

  const calculateAverageRating = () => {
    if (ratings.length === 0) return 0;
    const sum = ratings.reduce((acc, curr) => acc + curr.rating, 0);
    return sum / ratings.length;
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp || !timestamp.toDate) return 'Data não disponível';
    try {
      return format(timestamp.toDate(), "d 'de' MMMM 'de' yyyy", {
        locale: ptBR,
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Data não disponível';
    }
  };

  if (loading) {
    return <div>Carregando avaliações...</div>;
  }

  const averageRating = calculateAverageRating();

  return (
    <Card className="p-6">
      <div className="space-y-6">
        {/* Header with average rating */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Avaliações</h2>
          <div className="flex items-center gap-2">
            <div className="flex items-center">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`h-5 w-5 ${
                    star <= averageRating
                      ? 'text-yellow-400 fill-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            <span className="font-medium">
              {averageRating.toFixed(1)} ({ratings.length} avaliações)
            </span>
          </div>
        </div>

        {/* Ratings list */}
        <div className="space-y-4">
          {ratings.length === 0 ? (
            <p className="text-center text-gray-500 py-4">
              Este profissional ainda não possui avaliações
            </p>
          ) : (
            ratings.map((rating) => (
              <div
                key={rating.id}
                className="border rounded-lg p-4 space-y-2"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{rating.authorName}</span>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-500">
                        {formatDate(rating.createdAt)}
                      </span>
                    </div>
                    <div className="flex items-center mt-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`h-4 w-4 ${
                            star <= rating.rating
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  {(currentUserId === rating.authorId || currentUserId === providerId) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setRatingToDelete(rating)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <p className="text-gray-700">{rating.comment}</p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!ratingToDelete} onOpenChange={() => setRatingToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir avaliação</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta avaliação? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRating}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}